// Does Square's drawer close agree with the Station closing count?
//
// Port of the Library cash page's closing check (Library public/js/
// cash-management.js, summarizeCount, from 8abbe5e4) and of the rule the
// cash-drawer-count edge function applies at submit. The count is the whole
// drawer. Staff then either close Square with that full amount still in the
// drawer, or first record a Paid Out for the deposit ("Nightly deposit"), so
// Square's close holds only the retained bank. Both are clean; any other close
// amount is a mis-key.
//
// The count usually comes BEFORE the Square close, so the server's stored
// square_amount_cents is almost always null (31 of 32 closings, 2026-09-24).
// get_cash_drawer_daily returns square_amount_live_cents instead: the day's
// LAST Square shift for the register (44 register-days had more than one).
// Square fills closed_cash_cents only in state CLOSED; OPEN and ENDED (ended
// in Square but never closed) have none.

export type CheckTone = "ok" | "bad" | "pending" | "info";

export interface CheckLine {
  tone: CheckTone;
  text: string;
}

export type SquareShiftState = "OPEN" | "ENDED" | "CLOSED";

export interface SquareCloseInput {
  totalCents: number;
  /** null on closes saved before the wizard split (legacy rows). */
  retainedCents: number | null;
  depositCents: number | null;
  /** Square's close amount; null until Square marks the shift CLOSED. */
  squareCloseCents: number | null;
  squareExpectedCents: number | null;
  /** State of the day's last Square shift for the register; null = none found. */
  squareState: SquareShiftState | null;
}

interface CountLike {
  total_cents: number;
  retained_cents?: number | null;
  deposit_cents?: number | null;
  square_amount_cents?: number | null;
  square_expected_cents?: number | null;
  square_amount_live_cents?: number | null;
  square_expected_live_cents?: number | null;
}

interface ShiftLike {
  device_id: string;
  state: string;
  opened_at: string;
}

const money = (cents: number) => "$" + (Math.abs(cents) / 100).toFixed(2);

/** Builds the check input from a get_cash_drawer_daily count row and the day's shifts. */
export function squareCloseInput(count: CountLike, deviceId: string, shifts: ShiftLike[]): SquareCloseInput {
  const last = shifts
    .filter((s) => s.device_id === deviceId)
    .sort((a, b) => (a.opened_at < b.opened_at ? -1 : a.opened_at > b.opened_at ? 1 : 0))
    .pop();
  const state = last && (last.state === "OPEN" || last.state === "ENDED" || last.state === "CLOSED")
    ? (last.state as SquareShiftState)
    : null;
  // get_cash_drawer_daily always sends the live keys, null while the last
  // shift is open. Fall back to the figures stored at submit only when a
  // response lacks them: a stored close can belong to an earlier shift.
  const hasLive = "square_amount_live_cents" in count;
  return {
    totalCents: count.total_cents || 0,
    retainedCents: count.retained_cents ?? null,
    depositCents: count.deposit_cents ?? null,
    squareCloseCents: (hasLive ? count.square_amount_live_cents : count.square_amount_cents) ?? null,
    squareExpectedCents: (hasLive ? count.square_expected_live_cents : count.square_expected_cents) ?? null,
    squareState: state,
  };
}

/** Of the acceptable amounts, the difference from the one nearest to `actual`. */
function nearestDiff(actual: number, acceptable: number[]): number {
  return acceptable
    .map((a) => a - actual)
    .reduce((best, d) => (Math.abs(d) < Math.abs(best) ? d : best));
}

const overShort = (d: number) => `${money(d)} ${d > 0 ? "over" : "short"}`;

/** The Paid Out step, worded for what is still left to do in Square. */
export function squareNextStep(depositCents: number | null): string {
  return (depositCents || 0) > 0
    ? `Next in Square: record a Paid Out of ${money(depositCents || 0)} (note: "Nightly deposit"), then close the drawer.`
    : "Next in Square: close the drawer.";
}

export function squareCloseCheck(input: SquareCloseInput): CheckLine[] {
  const { totalCents: total, retainedCents: kept, squareCloseCents: sq, squareExpectedCents: exp } = input;
  const acceptable = kept != null && kept !== total ? [total, kept] : [total];
  const lines: CheckLine[] = [];

  if (sq != null) {
    if (sq === total) {
      lines.push({ tone: "ok", text: `Square close matches the count (${money(sq)}).` });
    } else if (kept != null && sq === kept) {
      lines.push({ tone: "ok", text: `Square close matches the ${money(kept)} bank kept, so the deposit was paid out.` });
    } else {
      // Positive = Square's close is higher than the nearest acceptable amount.
      const d = -nearestDiff(sq, acceptable);
      const should = acceptable.length === 2
        ? `${money(total)}, or ${money(kept as number)} if the deposit was paid out`
        : money(total);
      lines.push({
        tone: "bad",
        text: `Square close ${money(sq)} is ${money(d)} ${d > 0 ? "higher" : "lower"} than it should be (${should}). Check the amount entered in Square.`,
      });
    }
  } else if (input.squareState === "OPEN" || input.squareState === "ENDED") {
    lines.push({
      tone: "pending",
      text: input.squareState === "OPEN"
        ? "Square drawer not closed yet."
        : "Square shift was ended but the drawer was never closed in Square.",
    });
    lines.push({ tone: "info", text: squareNextStep(input.depositCents) });
  } else {
    lines.push({ tone: "info", text: "No Square shift found for this register on this day." });
  }

  // Square's expected cash nets out paid-outs: with the deposit Paid Out
  // recorded it should equal the bank kept, without it the full count. A
  // Square close that matches one of the two says which applies. Picking the
  // nearer amount instead flips the sign on any over/short above half the
  // deposit, and calls "Paid Out recorded AND closed at the full count"
  // balanced while Square's own report shows the deposit as over.
  if (exp != null) {
    const basis = sq != null && sq === total ? [total]
      : sq != null && kept != null && sq === kept ? [kept]
      : acceptable;
    // Until Square closes, sales can still move the expected amount.
    const tone: CheckTone = sq != null ? "bad" : "info";
    if (basis.includes(exp)) {
      lines.push({ tone: "ok", text: `Balanced with Square's expected cash (${money(exp)}).` });
    } else if (basis.length === 1) {
      // Positive = the drawer holds more than Square expects.
      const d = basis[0] - exp;
      lines.push({ tone, text: `${d > 0 ? "Over" : "Short"} by ${money(d)} against Square's expected cash (${money(exp)}).` });
    } else {
      // Unknown whether the Paid Out is in Square yet: give both readings.
      lines.push({
        tone,
        text: `Not balanced with Square's expected cash (${money(exp)}): the drawer is ${overShort(total - exp)}, or ${overShort((kept as number) - exp)} if the deposit Paid Out is already in Square.`,
      });
    }
  }
  return lines;
}
